import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useFlashMessage } from '@indigo-ag/component-module';
import i18next from 'core/i18n';
import {
  getCreditApprovalData,
  updateCreditApprovalRoundConditionals
} from 'features/brazil-grower/queries/creditApproval.queries';
import { UpdateCreditApprovalRoundConditionalsInput, UpdateCreditConditionalInput } from '@indigo-ag/schema';

interface UseSaveCreditConditionalParams {
  agFinancingId: string;
  roundId: string;
  selectedConditionals: UpdateCreditConditionalInput[];
  callback?: () => void;
}

export function useSaveCreditConditional() {
  const { addFlashMessage } = useFlashMessage();
  const [updateCreditApprovalRoundConditionalsFn, { loading }] = useMutation(updateCreditApprovalRoundConditionals);

  const saveCreditConditional = useCallback(
    async ({ agFinancingId, roundId, selectedConditionals, callback }: UseSaveCreditConditionalParams) => {
      try {
        const input: UpdateCreditApprovalRoundConditionalsInput = {
          selectedConditionals: [...selectedConditionals]
        };

        await updateCreditApprovalRoundConditionalsFn({
          variables: {
            creditApprovalRoundId: roundId,
            creditApprovalRoundConditionalsInput: input
          },
          refetchQueries: [
            {
              query: getCreditApprovalData,
              variables: { agFinancingId }
            }
          ],
          awaitRefetchQueries: true
        });

        addFlashMessage({
          content: '',
          title: i18next.t('creditConditionalsSuccessfullyUpdated'),
          variant: 'success'
        });

        if (callback) {
          callback();
        }
      } catch (error) {
        addFlashMessage({
          content: i18next.t('creditConditionals-errorUpdating'),
          title: '',
          variant: 'error'
        });
      }
    },
    [addFlashMessage, updateCreditApprovalRoundConditionalsFn]
  );

  return { saveCreditConditional, loading };
}
